import { useMemo } from 'react';

import { useStoreContext } from 'features/store';

import { useCart } from 'hooks/use-cart';
import { emptyCartContext, useCartContext } from 'state/cart';
import { selectors, useAppSelector } from 'state/global-state';
import { OrderStatus, useOrderContext } from 'state/order';

import { useStoreClosedReason } from './use-store-closed-reason';

export const useIsStoreUnavailable = () => {
  const { store: activeStore, isSelectingStore } = useStoreContext();
  const { calculateCartTotalWithDiscount } = useCart();
  const { fetchingPosData } = useOrderContext();
  const {
    serverOrder: serverOrderCart,
    unavailableCartEntries,
    priceOrderFailure,
    priceMutationError,
    orderStatus,
    shouldSkipPriceOrder,
    hydrationStatus,
    isHydrationPending,
  } = useCartContext();
  const { serverOrder: serverOrderContext } = useOrderContext();

  const serverOrder = serverOrderCart || serverOrderContext;
  const evaluatingIncentives = useAppSelector(selectors.loyalty.selectOffersLoading);
  const { isCartTotalNegative } = calculateCartTotalWithDiscount();
  const isInCart = useCartContext() !== emptyCartContext;

  const isPriceRequested = !serverOrder || orderStatus === OrderStatus.PRICE_REQUESTED;
  const isOrderPriceLoading =
    isInCart && !isCartTotalNegative && isPriceRequested && !shouldSkipPriceOrder;
  const isPriceLoading = isOrderPriceLoading || evaluatingIncentives;
  const hasPricingErrors = !!priceMutationError || priceOrderFailure || hydrationStatus?.error;

  const { closedReason, closedReasonDetails } = useStoreClosedReason(activeStore);

  const isStoreUnavailable = useMemo(() => {
    return !!(activeStore?.number && !fetchingPosData && closedReason !== null);
  }, [activeStore?.number, fetchingPosData, closedReason]);

  const errorConditions = useMemo(
    () => ({
      hasPricingErrors: hasPricingErrors && !isCartTotalNegative,
      isStoreUnavailable,
    }),
    [hasPricingErrors, isCartTotalNegative, isStoreUnavailable]
  );

  const isLoadingFinished = !isPriceLoading && !isHydrationPending && !isSelectingStore;

  const shouldShowUnavailableDialog = useMemo(() => {
    return (
      isLoadingFinished &&
      !unavailableCartEntries?.length &&
      Object.values(errorConditions).some(Boolean)
    );
  }, [isLoadingFinished, unavailableCartEntries?.length, errorConditions]);

  return {
    isStoreUnavailable,
    closedReason,
    closedReasonDetails,
    errorConditions,
    hasPricingErrors,
    priceMutationError,
    priceOrderFailure,
    hydrationStatus,
    shouldShowUnavailableDialog,
  };
};
